import React, { useEffect, useState } from 'react';
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Link,
} from '@react-pdf/renderer';
import logo from '../../images/57logo.jpeg';
import { formatTime, calculateWorkingHours } from '../Utils/Utils';

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#112131',
    paddingBottom: 10,
  },
  companyInfo: {
    flexDirection: 'column',
    alignItems: 'flex-end',
  },
  logo: {
    width: 100,
    height: 80,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#112131',
    marginBottom: 5,
  },
  subtitle: {
    fontSize: 12,
    color: '#666',
  },
  siteInfo: {
    marginVertical: 15,
    padding: 15,
    backgroundColor: '#f8f8f8',
    borderRadius: 5,
  },
  infoRow: {
    flexDirection: 'row',
    marginBottom: 5,
  },
  infoLabel: {
    width: 100,
    fontWeight: 'bold',
    fontSize: 12,
  },
  infoValue: {
    fontSize: 12,
  },
  table: {
    marginTop: 20,
    borderWidth: 1,
    borderColor: '#112131',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#112131',
    color: 'white',
    fontSize: 10,
    fontWeight: 'bold',
  },
 
  tableRowUnassigned: {
    backgroundColor: '#fff7cc', // light yellow for unassigned users
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#ccc',
    alignItems: 'flex-start',
    paddingVertical: 4,
  },
  tableCell: {
    padding: 4,
    fontSize: 10,
    textAlign: 'left',
    flexWrap: 'wrap',
    minHeight: 28,
  },
  tableCellHeader: {
    padding: 4,
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'left',
    color: 'white',
    flexWrap: 'wrap',
  },
  
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 30,
    right: 30,
    fontSize: 10,
    textAlign: 'center',
    color: '#666',
    borderTopWidth: 1,
    borderTopColor: '#eee',
    paddingTop: 10,
  },
});

const columnFlex = {
  index: 0.5,
  staffId: 0.8,
  name: 1.5,
  role: 1.5,
  siteIn: 1,
  siteOut: 1,
  totalHrs: 1,
  entryLoc: 1.2,
  exitLoc: 1.2,
};


const SiteProfilePDF = ({ site, assignedUsers, address, presentUsers }) => {
  const date = new Date();
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const currentDate = `${day}/${month}/${year}`;

  const [finalUsers, setFinalUsers] = useState([]);

  useEffect(() => {
    const assignedUserIds = assignedUsers.map((u) => u?.id).filter(Boolean);

    const assignedWithFlag = assignedUsers.map((user) => ({
      ...user,
      isAssigned: true,
    }));

    const extraUsers = presentUsers
      .filter((pUser) => !assignedUserIds.includes(pUser.id))
      .map((user) => ({
        ...user,
        isAssigned: false,
      }));

    setFinalUsers([...assignedWithFlag, ...extraUsers]);
  }, [assignedUsers, presentUsers]);


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View>
            <Text style={styles.title}>Site Attendance Report</Text>
            <Text style={styles.subtitle}>Generated on: {currentDate}</Text>
          </View>
          <View style={styles.companyInfo}>
            <Image style={styles.logo} src={logo} />
            <Text style={styles.subtitle}>57Pease Protection Limited</Text>
          </View>
        </View>

        {/* Site Info */}
        <View style={styles.siteInfo}>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Site Name:</Text>
            <Text style={styles.infoValue}>{site.name || '-'}</Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Address:</Text>
            <Text style={styles.infoValue}>
              {`${address[0]?.unitNo || ''} ${address[0]?.street || ''}, ${address[0]?.city || ''}, ${address[0]?.country || ''}`.trim() || '-'}
            </Text>
          </View>
          <View style={styles.infoRow}>
            <Text style={styles.infoLabel}>Total Staff:</Text>
            <Text style={styles.infoValue}>{assignedUsers.length || 0}</Text>
          </View>
        </View>

        {/* Table */}
        <View style={styles.table}>
          {/* Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.index }]}>#</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.staffId }]}>Staff ID</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.name }]}>Name</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.role }]}>Role</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.siteIn }]}>Time In</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.siteOut }]}>Time Out</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.totalHrs }]}>Total Hrs</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.entryLoc }]}>Time In Location</Text>
            <Text style={[styles.tableCellHeader, { flex: columnFlex.exitLoc }]}>Time Out Location</Text>
          </View>

          {/* Rows */}
          {finalUsers.map((user, index) => (
            <View
              key={index}
              style={[
                styles.tableRow,
                user.isAssigned === false && styles.tableRowUnassigned,
              ]}
            >
              <Text style={[styles.tableCell, { flex: columnFlex.index }]}>{index + 1}</Text>
              <Text style={[styles.tableCell, { flex: columnFlex.staffId }]}>{user.id || '-'}</Text>
              <Text style={[styles.tableCell, { flex: columnFlex.name }]}>
                {`${user.firstName || ''} ${user.lastName || ''}`.trim() || '-'}
              </Text>
              <Text style={[styles.tableCell, { flex: columnFlex.role }]}>{user.roleName || '-'}</Text>
              <Text style={[styles.tableCell, { flex: columnFlex.siteIn }]}>{user.entryTime ? formatTime(user.entryTime) : '-'}</Text>
              <Text style={[styles.tableCell, { flex: columnFlex.siteOut }]}>{user.exitTime ? formatTime(user.exitTime) : '-'}</Text>
              <Text style={[styles.tableCell, { flex: columnFlex.totalHrs }]}>{user.entryTime && user.exitTime ? (calculateWorkingHours(user.entryTime, user.exitTime)) : '-'}</Text>
              <Text style={[styles.tableCell, { flex: columnFlex.entryLoc }]}>
                {user.entryMapUrl ? (
                  <Link src={user.entryMapUrl} style={{ color: '#0066cc' }}>
                    View
                  </Link>
                ) : (
                  '-'
                )}
              </Text>
              <Text style={[styles.tableCell, { flex: columnFlex.exitLoc }]}>
                {user.exitMapUrl ? (
                  <Link src={user.exitMapUrl} style={{ color: '#0066cc' }}>
                    View
                  </Link>
                ) : (
                  '-'
                )}
              </Text>
            </View>
          ))}
        </View>

        {/* Footer */}
        <View style={styles.footer}>
          <Text>57Pease Protection Limited • {currentDate} • Confidential</Text>
        </View>
      </Page>
    </Document>
  );
};

export default SiteProfilePDF;
